/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { useTranslations } from '@unisporkal/localization';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';
import type { Price } from 'types/isomorphic';
import useOriginalSize from '../../../../shared/hooks/useOriginalSize';
import useSelectedIndex from '../../../../hooks/stateful/useSelectedIndex';
import { Checkmark } from '../support/icons';
import OriginalImageRow from './OriginalImageRow';
import styles from '../ModifiedBuyCard.module.scss';

interface OriginalImageProps {
  sizes: Price[];
  priced: boolean;
  defaultToLastSize?: boolean;
  // Extend edits: rows detail the joined modified rendition in place (no
  // separate modified section), and the priced list drops its section header
  // to match the legacy GenAssetSizes drawer.
  showModifiedJoin?: boolean;
}

const OriginalImage = ({
  sizes,
  priced,
  defaultToLastSize = false,
  showModifiedJoin = false,
}: OriginalImageProps) => {
  const t = useTranslations();
  const { originalSize, setOriginalSize } = useOriginalSize();
  const { setSelectedSizeByName } = useSelectedIndex();
  const [editing, setEditing] = useState(false);

  // In the priced (ALC) flow the licensed original size drives price, sizeCode
  // and tracking via useSelectedIndex, so keep it in sync with the user's pick.
  // The download ("also included") flow is entitled, so it stays untouched.
  const selectOriginalSize = (size: Price) => {
    setOriginalSize(size);
    if (priced) setSelectedSizeByName(size.name);
  };

  // Default-select an original size so the request always carries one. The
  // fibo flow keeps its established first-size default; extend mirrors the
  // legacy GenAssetSizes default (useSelectedIndex picks the LAST size, the
  // smallest/cheapest as served), so the drawer opens on the same size and
  // price as before the migration.
  useEffect(() => {
    if (sizes.length > 0 && !originalSize?.name)
      selectOriginalSize(
        defaultToLastSize ? sizes[sizes.length - 1] : sizes[0]
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizes, originalSize]);

  if (sizes.length === 0) return null;

  const selectedName = originalSize?.name || sizes[0]?.name;
  const selected = sizes.find((s) => s.name === selectedName) || sizes[0];
  const selectedDims =
    selected.pixelWidth && selected.pixelHeight
      ? t('img_size_in_pixels', {
          width: selected.pixelWidth,
          height: selected.pixelHeight,
        })
      : '';

  // embedded: rendered inside the already-bordered "Also included" container
  // (download flow), so it drops its own border and just divides off the
  // summary above it. Standalone (priced flow) it is its own bordered card.
  const sizeList = (showPrice: boolean, embedded = false) => (
    <div className={embedded ? styles.includedSizeList : styles.optionsCard}>
      {sizes.map((size) => (
        <OriginalImageRow
          key={size.name}
          size={size}
          selected={selectedName === size.name}
          onSelect={(s) => {
            selectOriginalSize(s);
            setEditing(false);
          }}
          showPrice={showPrice}
          showModifiedJoin={showModifiedJoin}
        />
      ))}
    </div>
  );

  // Extend edits render the legacy GenAssetSizes drawer shape in BOTH flows:
  // a bare size list (no section header) whose selected row details the
  // joined rendition — prices only in the priced flow.
  if (showModifiedJoin) {
    return <section data-testid="original-image">{sizeList(priced)}</section>;
  }

  return (
    <section data-testid="original-image">
      <Typography
        component={TypographyComponentEnum.DIV}
        variant={TypographyVariant.body.small}
        className={styles.sectionHeader}
      >
        {priced ? t('select_original_image_size') : t('preshot_also_included')}
      </Typography>

      {priced ? (
        sizeList(true)
      ) : (
        <div className={cx(styles.originalWrapper, styles.optionsCard)}>
          <div className={styles.includedSummary}>
            <Checkmark className={styles.checkmarkIcon} />
            <div className={styles.includedText}>
              <Typography
                component={TypographyComponentEnum.DIV}
                variant={TypographyVariant.body.small}
              >
                {t('preshot_original_image')}
              </Typography>
              <Typography
                component={TypographyComponentEnum.DIV}
                variant={TypographyVariant.body.xsmall}
                className={styles.dimensionsLine}
              >
                {t('preshot_unmodified_version_description')}
              </Typography>
              <div className={styles.includedSizeRow}>
                {selectedDims && (
                  <Typography
                    component={TypographyComponentEnum.SPAN}
                    variant={TypographyVariant.body.xsmall}
                    className={styles.dimensionsLine}
                  >
                    {selectedDims}
                  </Typography>
                )}
                <Button
                  type="button"
                  variant={ButtonVariantEnum.LINK}
                  size={ButtonSizeEnum.EXTRA_SMALL}
                  className={styles.changeButton}
                  onClick={() => setEditing((prev) => !prev)}
                >
                  {t('change_capitalized')}
                </Button>
              </div>
            </div>
          </div>
          {editing && sizeList(false, true)}
        </div>
      )}
    </section>
  );
};

export default OriginalImage;
